import type { TransactionCurrency } from '@contracts/payment/Currency';
import { CurrencyField } from '@src/clients/financialSettings/fields/CurrencyFields';
import { useAvailableCurrencies } from '@src/clients/financialSettings/hooks/useAvailableCurrencies';
import { NoCurrencyAvailable } from '@src/clients/financialSettings/NoCurrencyAvailable';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import { useEffect } from 'react';
import type { Control, FieldValues } from 'react-hook-form';
import type { UseFormSetValue } from 'react-hook-form/dist/types/form';
import { useTranslation } from 'react-i18next';

type CurrencyFieldValue = FieldValues & {
  [CurrencyField.fieldName]: keyof typeof TransactionCurrency | undefined;
};

interface Props<T extends CurrencyFieldValue> {
  control: Control<T>;
  disabled?: boolean;
  defaultCurrency?: TransactionCurrency;
  validateOnAvailableDefaultCurrency?: boolean;
  setValue: UseFormSetValue<T>;
}

export const AvailableCurrencyDropdownWithOptionalDefault = <
  T extends CurrencyFieldValue,
>({
  control,
  disabled = false,
  defaultCurrency,
  validateOnAvailableDefaultCurrency,
  setValue,
}: Props<T>) => {
  const { t } = useTranslation();
  const { currencies, isLoading } = useAvailableCurrencies();
  const displayDropdown = isLoading || currencies.length > 0;
  const isDefaultCurrencyValid =
    defaultCurrency === undefined ? true : currencies.includes(defaultCurrency);

  useEffect(() => {
    if (isDefaultCurrencyValid) {
      // @ts-ignore
      setValue(CurrencyField.fieldName, defaultCurrency, {
        shouldValidate: validateOnAvailableDefaultCurrency ?? false,
      });
    }
  }, [
    isDefaultCurrencyValid,
    defaultCurrency,
    setValue,
    validateOnAvailableDefaultCurrency,
  ]);

  const displayError =
    !isLoading && (currencies.length === 0 || !isDefaultCurrencyValid);

  return (
    <>
      {displayDropdown && (
        <FormDropdown
          fieldName={CurrencyField.fieldName}
          control={control as Control<CurrencyFieldValue>}
          disabled={isLoading || disabled}
          label={t('fields.currency')}
          options={currencies.map((currency: TransactionCurrency) => ({
            label: t(`currency.${currency.toLowerCase()}`),
            value: currency.toUpperCase(),
          }))}
        />
      )}
      {displayError && (
        <Stack flex={1}>
          <NoCurrencyAvailable />
        </Stack>
      )}
    </>
  );
};
